import { currentDate_dmy, CurrentTime, loginCompanyName, loginUserDetails } from "../../components/Common/CommonFunction";
import { numberWithCommas } from "../Report_common_function";
import * as table from "./TableData";

/**
 * OrderReportStyle.js — RA Masale format (Purchase/Sales Order print)
 * ------------------------------------------------------------------
 * All doc.* drawing calls live here, same split as OrderTableData.js
 * / ReportStyle.js. OrderPage.js wires these together in order.
 *
 * Differences from the invoice ReportStyle.js:
 *   - Letterhead prefers the Order's own Supplier* fields (present
 *     directly on the order payload), falling back to
 *     loginUserDetails()/loginCompanyName() only if those are absent.
 *   - No IRN/Ack row — orders aren't e-invoiced documents.
 *   - Footer uses data.OrderAmount as the grand total (orders have
 *     no RoundOffAmount field, so it defaults to 0).
 * ------------------------------------------------------------------
 */

const getDefaultCompanyDetails = (data) => {
    const user = loginUserDetails() || {};
    const companyName = loginCompanyName();

    return {
        GSTIN: data.SupplierGSTIN || user.GSTIN || "",
        Name:  companyName || "",
        AddressLine1: [data.SupplierAddress, data.SupplierPIN].filter(Boolean).join(" - ") || user.PartyAddress || "",
        FSSAILicNo: data.SupplierFssai || user.FSSAINo || "",
        PAN: "",
        CIN: "",
    };
};

export const pageBorder = (doc) => {
    doc.setDrawColor(0, 0, 0);
    doc.setLineWidth(0.75);
    doc.rect(30, 20, 540, 795);
    doc.setLineWidth(0.5);
};

const drawTightLabelValue = (doc, label, value, x, y, { boldLabel = true, gap = 4 } = {}) => {
    doc.setFont("helvetica", boldLabel ? "bold" : "normal");
    doc.text(`${label} :`, x, y);
    const labelWidth = doc.getTextWidth(`${label} :`);

    doc.setFont("helvetica", "normal");
    const valueText = ` ${value ?? ""}`;
    doc.text(valueText, x + labelWidth + gap, y);
    const valueWidth = doc.getTextWidth(valueText);

    return x + labelWidth + gap + valueWidth;
};

export const pageHeder = (doc, data, companyDetails) => {
    const defaultCompanyDetails = getDefaultCompanyDetails(data);

    const company = {
        ...defaultCompanyDetails,
        ...(companyDetails || {}),
    };

    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.text(`GSTIN :  ${company.GSTIN}`, 38, 34);

    doc.setFont("helvetica", "italic");
    doc.setFontSize(9);
    doc.text("Original Copy", 500, 34);

    doc.setFont("helvetica", "bold");
    doc.setFontSize(13);
    doc.text("ORDER", 300, 34, { align: "center" });

    doc.setFontSize(20);
    doc.text(company.Name, 300, 55, { align: "center" });

    doc.setFont("helvetica", "normal");
    doc.setFontSize(8.5);
    doc.text(company.AddressLine1, 300, 68, { align: "center" });

    doc.setFont("helvetica", "bold");
    doc.setFontSize(7.5);
    doc.text(
        `FSSAI Lic. No. : ${company.FSSAILicNo}  PAN - ${company.PAN}  CIN NO - ${company.CIN}`,
        300,
        78,
        { align: "center" }
    );

    doc.setDrawColor(0, 0, 0);
    doc.line(30, 86, 570, 86);
};

/**
 * Two-column block: Party Details (customer/buyer) on the left,
 * order meta info on the right. No IRN row follows this on the
 * order print, so the returned bottomY feeds straight into the
 * items table.
 */
export const reportPartyAndOrderInfo = (doc, data) => {
    const startY = 98;
    const leftX = 34;
    const rightX = 305;
    const lineH = 12;

    // ---- left column: Party Details ----
    const party = table.PartyDetails(data);
    let y = startY;

    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.text("Party Details :", leftX, y);
    y += lineH;

    doc.setFont("helvetica", "bold");
    doc.setFontSize(8.5);
    doc.text(party.name, leftX, y);
    y += lineH - 1;

    doc.setFont("helvetica", "normal");
    const wrappedAddress = doc.splitTextToSize(party.address, 270);
    doc.text(wrappedAddress, leftX, y);
    y += wrappedAddress.length * (lineH - 1);

    const wrappedGstinBlock = doc.splitTextToSize(party.gstinLine, 270);
    doc.text(wrappedGstinBlock, leftX, y);
    y += wrappedGstinBlock.length * (lineH - 1);

    const leftBottomY = y;

    // ---- right column: Order Info (tight label:value spacing) ----
    doc.setFontSize(8.5);
    const infoRows = table.OrderInfoRows(data);
    let ry = startY;

    infoRows.forEach((row) => {
        const endX = drawTightLabelValue(doc, row.label, row.value, rightX, ry);

        if (row.extraLabel) {
            drawTightLabelValue(doc, row.extraLabel, row.extraValue, endX + 12, ry);
        }
        ry += lineH;
    });

    const bottomY = Math.max(leftBottomY, ry) + 4;

    doc.setDrawColor(0, 0, 0);
    doc.line(30, bottomY, 570, bottomY);
    doc.line(300, startY - 10, 300, bottomY);

    return bottomY;
};

export const tableBody = (doc, data, startY) => {
    const w = table.columnWidths;

    const options = {
        margin: { left: 30, right: 30, bottom: 120 },
        theme: "grid",
        startY: startY,
        tableWidth: "wrap",
        headStyles: {
            fillColor: [255, 255, 255],
            textColor: [0, 0, 0],
            fontStyle: "bold",
            halign: "center",
            fontSize: 8,
            lineColor: [0, 0, 0],
            lineWidth: 0.5,
        },
        bodyStyles: {
            fontSize: 7.5,
            textColor: [20, 20, 20],
            lineColor: [0, 0, 0],
            lineWidth: 0.5,
            cellPadding: 3,
            overflow: "linebreak",
        },
        columnStyles: {
            0: { cellWidth: w[0], halign: "center" },
            1: { cellWidth: w[1], halign: "left" },
            2: { cellWidth: w[2], halign: "center" },
            3: { cellWidth: w[3], halign: "right" },
            4: { cellWidth: w[4], halign: "right" },
            5: { cellWidth: w[5], halign: "center" },
            6: { cellWidth: w[6], halign: "right" },
            7: { cellWidth: w[7], halign: "center" },
            8: { cellWidth: w[8], halign: "right" },
            9: { cellWidth: w[9], halign: "right" },
        },
    };

    doc.autoTable(table.columns, table.Rows(data), options);
    return doc.previousAutoTable.finalY;
};

/**
 * Footer totals — same stacked CGST / SGST (or IGST) / Round-Off
 * layout as the invoice. Orders have no RoundOffAmount field so it
 * defaults to 0, and the grand total comes from data.OrderAmount
 * (not data.GrandTotal).
 */
export const reportFooterTotals = (doc, data, startY) => {
    const { totalCGST = 0, totalSGST = 0, totalIGST = 0, isIGST, totalQty = 0 } =
        data.footerTotals || {};

    let y = startY + 16;
    doc.setDrawColor(0, 0, 0);
    doc.line(30, startY + 4, 570, startY + 4);
    doc.line(310, startY + 4, 310, y + 34);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8.5);

    if (isIGST) {
        doc.text("Total IGST Amount", 320, y);
        doc.text(numberWithCommas(Number(totalIGST).toFixed(2)), 560, y, { align: "right" });
        y += 12;
    } else {
        doc.text("Total CGST Amount", 320, y);
        doc.text(numberWithCommas(Number(totalCGST).toFixed(2)), 560, y, { align: "right" });
        y += 12;

        doc.text("Total SGST Amount", 320, y);
        doc.text(numberWithCommas(Number(totalSGST).toFixed(2)), 560, y, { align: "right" });
        y += 12;
    }

    doc.setFont("helvetica", "italic");
    doc.text("Add : Rounded Off (+)", 320, y);
    doc.setFont("helvetica", "normal");
    doc.text(numberWithCommas(Number(data.RoundOffAmount || 0).toFixed(2)), 560, y, { align: "right" });
    doc.line(30, y + 10, 570, y + 10);
    y += 12;

    doc.setFont("helvetica", "bold");
    doc.text("Totals c/o", 34, y + 11);
    doc.text(`${numberWithCommas(Number(totalQty).toFixed(2))} Units`, 200, y + 11, { align: "right" });
    doc.text(numberWithCommas(Number(data.OrderAmount || 0).toFixed(2)), 560, y + 11, { align: "right" });

    return y + 6;
};

export const pageFooter = (doc) => {
    const pageCount = doc.internal.getNumberOfPages();
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7.5);
    for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        pageBorder(doc);
        doc.text(`Page ${i} of ${pageCount}`, 520, 825);
        doc.text(
            `Print Date :${String(currentDate_dmy)} Time ${String(CurrentTime())}`,
            34,
            825
        );
    }
};